import { createContext, useCallback, useContext, useEffect, useId, useMemo, type ReactNode } from 'react'
import useToggle from 'hooks/useToggle'

const CRUISE_SEARCH_INPUT_OPEN_EVENT = 'cruise-search-input-open'

/** Shared open-state for cruise search filter inputs (desktop dropdown vs mobile drawer). */
export interface CruiseSearchInputContextValue {
  /** True when either desktop dropdown or mobile drawer is open */
  isOpen: boolean
  /** Desktop dropdown sheet open state */
  isDesktopOpen: boolean
  open: () => void
  close: () => void

  /** Mobile / tablet drawer open state */
  isMobileOpen: boolean
  openMobile: () => void
  closeMobile: () => void
  toggleMobile: () => void
}

const CruiseSearchInputContext = createContext<CruiseSearchInputContextValue | null>(null)

export interface CruiseSearchInputProviderProps {
  children: ReactNode
  /** Sets both surfaces initial open state (e.g. isolated list tests). */
  defaultPanelOpen?: boolean
  /** Desktop dropdown; ignored when defaultPanelOpen is set. */
  defaultOpen?: boolean
  /** Mobile drawer; ignored when defaultPanelOpen is set. */
  defaultMobileOpen?: boolean
}

export function CruiseSearchInputProvider({
  children,
  defaultPanelOpen,
  defaultOpen = false,
  defaultMobileOpen = false,
}: CruiseSearchInputProviderProps) {
  const providerId = useId()
  const desktopInitial = defaultPanelOpen !== undefined ? defaultPanelOpen : defaultOpen
  const mobileInitial = defaultPanelOpen !== undefined ? defaultPanelOpen : defaultMobileOpen

  const {
    value: isDesktopOpen,
    on: openDesktop,
    off: close,
  } = useToggle(desktopInitial)
  const {
    value: isMobileOpen,
    on: openMobilePanel,
    off: closeMobile,
    toggle: toggleMobile,
  } = useToggle(mobileInitial)

  const notifyOpen = useCallback(() => {
    window.dispatchEvent(new CustomEvent(CRUISE_SEARCH_INPUT_OPEN_EVENT, { detail: providerId }))
  }, [providerId])

  const open = useCallback(() => {
    notifyOpen()
    openDesktop()
  }, [notifyOpen, openDesktop])

  const openMobile = useCallback(() => {
    notifyOpen()
    openMobilePanel()
  }, [notifyOpen, openMobilePanel])

  const toggleMobileWithNotification = useCallback(() => {
    if (!isMobileOpen) {
      notifyOpen()
    }

    toggleMobile()
  }, [isMobileOpen, notifyOpen, toggleMobile])

  useEffect(() => {
    const handleInputOpen = (event: Event) => {
      if (!(event instanceof CustomEvent) || event.detail === providerId) {
        return
      }

      close()
      closeMobile()
    }

    window.addEventListener(CRUISE_SEARCH_INPUT_OPEN_EVENT, handleInputOpen)

    return () => window.removeEventListener(CRUISE_SEARCH_INPUT_OPEN_EVENT, handleInputOpen)
  }, [close, closeMobile, providerId])

  const value = useMemo(
    (): CruiseSearchInputContextValue => ({
      isOpen: isMobileOpen || isDesktopOpen,
      isDesktopOpen,
      open,
      close,
      isMobileOpen,
      openMobile,
      closeMobile,
      toggleMobile: toggleMobileWithNotification,
    }),
    [isDesktopOpen, open, close, isMobileOpen, openMobile, closeMobile, toggleMobileWithNotification],
  )

  return (
    <CruiseSearchInputContext.Provider value={value}>
      {children}
    </CruiseSearchInputContext.Provider>
  )
}

export function useCruiseSearchInput(): CruiseSearchInputContextValue {
  const ctx = useContext(CruiseSearchInputContext)
  if (ctx == null) {
    throw new Error('useCruiseSearchInput must be used within CruiseSearchInputProvider')
  }
  return ctx
}
